// @flow

import React, { Component } from 'react';
import {colorbrewer} from './3rd/custom_colorbrewer.js';
import _ from 'lodash';

import TablaEscala from './TablaEscala.jsx';
import App from './App.jsx';

import {gen_snum, takeDir, flatten_nums, fix_elem_name} from './utils.js';

import type {T_ReadEscalaRow} from './TablaEscala.jsx';
import type {T_EscalaLyr, T_NumPair} from './types.js';

import './css/EscalaLyr.css';


type T_EscalaLyrProps = {
  lyr         : T_EscalaLyr,
  lim         : ?[number, number],
  name_escala : string,
  num_colors  : number,
  inv_brewer  : boolean,
  num_decimals: number,
  png_path    : string,
  app         : App
};


type T_EscalaLyrState = {
  sub_has : number,
  percent : number,
  smile_on: number
};



function make_row(ii: number, tot: number, color: string, vmin: number, vmax: number, inv: boolean): T_ReadEscalaRow {
  if (ii === 0) {
    return {
      color,
      rel  : (inv ? '< ' : '> '),
      range: [inv ? vmin : vmax]
    };
  }
  else if (ii === tot - 1) {  // last
    return {
      color,
      rel  : (inv ? '> ' : '< '),
      range: [inv ? vmax : vmin]
    };
  }
  else {
    const delta = (inv ? 1 : -1) * (Math.abs(vmax - vmin) / (tot - 2)),
          tmin  = Math.min(vmax, vmin),
          tmax  = Math.max(vmax, vmin),
          start = inv ? tmin : tmax,
          an    = start + ((ii - 1) * delta),
          bn    = start + (ii * delta);
    
    return {
      color,
      rel  : '',
      range: [Math.min(an, bn), Math.max(an, bn)]
    };
  }
}



export default class EscalaLyr extends Component<T_EscalaLyrProps, T_EscalaLyrState> {
  table: ?TablaEscala;
  
  sel_changed: (hilight_top: boolean, hilight_middle: Array<number>, hilight_bottom: boolean) => void;
  
  
  constructor(props: T_EscalaLyrProps) {
    super(props);
    
    this.state = {
      sub_has : 0,
      percent : 0,
      smile_on: NaN
    };
    
    this.sel_changed = this.sel_changed.bind(this);
    
    props.app.escala_lyr = this;
  }
  
  
  
  componentWillUnmount() {
    this.props.app.escala_lyr = null;
  }
  
  
  
  reset_totals(): void {
    this.setState({
      sub_has: 0,
      percent: 0
    });
  }
  
  
  
  componentDidUpdate(prevProps: T_EscalaLyrProps, prevState: T_EscalaLyrState): void {
    if (prevProps.num_colors !== this.props.num_colors || prevProps.name_escala !== this.props.name_escala) {
      this.reset_totals();
    }
  }
  
  
  
  sel_changed(hilight_top: boolean, hilight_middle: Array<number>, hilight_bottom: boolean): void {
    const {lyr}  = this.props,
          [a, b] = lyr.elemRange,
          vmin   = Math.min(a, b),
          vmax   = Math.max(a, b),
          delta  = Math.abs(vmax - vmin) / (this.props.num_colors - 2),
          marks  = [-1].concat(_.range(this.props.num_colors - 2).map(ii => vmin + (delta * ii))).concat([vmax, 1e15]),
          pairs  = _.initial(_.zip(marks, _.tail(marks)));
          
    let indexes = hilight_middle.map(ii => ii + 1);
    
    if (hilight_top) {
      indexes.push(0);
    }
    
    if (hilight_bottom) {
      indexes.push(this.props.num_colors - 1);
    }
    
    if (! lyr.inverted) {
      indexes = indexes.map(ii => this.props.num_colors - ii - 1);
    }
    
    this.show_area_in(indexes.sort().map(ii => pairs[ii]));
  }
  
  
  
  show_area_in(ranges: Array<T_NumPair>): void {
    const {app} = this.props,
          path  = this.props.png_path;

    if (ranges.length === 0) {
      this.reset_totals();
      
      if (app.map_ui.map.Img_List[path]) {
        app.map_ui.map.Img_List[path].reload(app);
      }
    }
    else {
      const dir     = takeDir(path),
            culti   = app.map_ui.map.get_culti_of(dir),
            sranges = flatten_nums(ranges),
            url     = `${app.remote}/area-in-ranges/?png_path=${path}&ranges=${sranges}&session_id=${app.session()}`;
      
      fetch(url).then(resp => resp.json()).then((response: number | string) => {
        if (typeof response === 'number') {
          const new_state = {
            sub_has: response / 10000,
            percent: -1
          };
          
          if (culti !== '') {
            const total = app.map_ui.map.Shp_List[culti].obj.total_has;
            
            if (typeof total === 'number') {
              new_state.percent = (100 * new_state.sub_has) / total;
            }
          }
          
          this.setState(new_state, () => {
            if (app.map_ui.map.Img_List[path]) {
              app.map_ui.map.Img_List[path].reload(app, ranges);
            }
          });
        }
      });
    }
  }
  
  
  
  render() {
    let colors = colorbrewer[this.props.name_escala][this.props.num_colors];
    
    if (! colors) {
      console.log(`no colors for ${this.props.name_escala} ${this.props.num_colors}`);
      
      return null;
    }
    
    const {lyr}        = this.props,
          [vmin, vmax] = lyr.elemRange,
          snum         = gen_snum(this.props.num_decimals),
          snum1        = gen_snum(1);
    
    if (! this.props.inv_brewer) {                // <---- esa negación esta ok.
      colors = _.reverse(_.cloneDeep(colors));
    }
    
    const esc = {
      elem       : fix_elem_name(lyr.realName),
      unit       : lyr.units,
      rows       : colors.map((color, ii) => make_row(ii, colors.length, color, vmin, vmax, lyr.inverted)),
      sel_changed: this.sel_changed,
      name_escala: this.props.name_escala,
      smile_on   : this.state.smile_on,
      lim        : this.props.lim,
      snum
    };
    
    return (
      <div>
        <TablaEscala {...esc} ref={comp => this.table = comp} />
        
        <div className='esc_lyr'>
          <center>
            {(this.state.sub_has !== -1) &&
              <span>
                {snum1(this.state.sub_has)} has
              </span>
            }
            
            {(this.state.percent !== -1) &&
              <span className='esc_lyr_per'>
                {Math.round(this.state.percent)}%
              </span>
            }
          </center>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/EscalaLyr.jsx